import type { DrawData } from '../../../schema/draw/entry.js';
import type { Definition } from '../../../schema/frameworks/entry.js';
import type { DefaultedDrawQueryOptions } from '../common/queryKeys.js';
import { DEFAULT_DRAW_ID } from '../entry.js';

type Options = { definition: Definition; shouldUseDefaultFirstDraw?: boolean };

/**
 * Find the opened draw from the data.
 * Defaults to the first draw if no or incorrect query info is provided (can be overridden).
 *
 * @param productDraws Fetched draw data
 * @param queryInfo Query info containing drawNumber and product
 * @param root0 Options object
 * @param root0.definition Product definition model
 * @param root0.shouldUseDefaultFirstDraw shouldUseDefaultFirstDraw Should use the default first draw if no draw found
 * @returns Returns the opened draw
 * @throws {Error} if product is missing for product with multiple draw products
 */
export function findOpenedDraw(
  productDraws: DrawData[],
  queryInfo: Partial<DefaultedDrawQueryOptions> | undefined,
  { definition, shouldUseDefaultFirstDraw = true }: Options
): DrawData | undefined {
  const { drawNumber } = queryInfo ?? {};
  let { product } = queryInfo ?? {};
  let [currentDraw] = productDraws;

  if (!shouldUseDefaultFirstDraw) {
    currentDraw = undefined;
  }

  if (definition.drawProducts.length > 1 && drawNumber && !product) {
    throw new Error(`Product param is missing for product with multiple draw products: ${definition.moduleEngine}`);
  }

  // Always assume the function is called with a product if there is multiple draw products
  if (!product) {
    [product] = definition.drawProducts;
  }

  if (drawNumber && drawNumber !== DEFAULT_DRAW_ID && product) {
    // Try to find the open draw or else return undefined if not found, this will be handled in the UI layer
    currentDraw = productDraws.find(
      (productDraw) => productDraw.draw.product === product && productDraw.draw.drawNumber === drawNumber
    );
  }

  return currentDraw;
}
